/**
 * The basemap, in one place.
 *
 * CARTO's dark_matter was the original choice and had to be replaced: CARTO began requiring an
 * API key and now serves a tile with "API KEY REQUIRED" *stamped into the image*, at HTTP 200.
 * Nothing in the app could detect that — no error, no status code, a perfectly valid PNG — so
 * the only signal was somebody looking at the map. Assume any free tile provider can do the
 * same, and keep the swap to one edit.
 *
 * Requirements for anything used here: no API key, no account, no domain registration, and a
 * dark palette (the altitude colours have to be the only saturated thing on screen).
 */

export type Basemap = {
  url: string;
  attribution: string;
  subdomains?: string;
  /** Highest zoom the provider actually serves tiles for. */
  maxNativeZoom: number;
  /** Highest zoom the map allows — Leaflet upscales native tiles past maxNativeZoom. */
  maxZoom: number;
};

/** Esri World Dark Gray Canvas. Keyless, purpose-built dark cartography, stops at z16. */
export const ESRI_DARK_GRAY: Basemap = {
  url: "https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
  attribution: "Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ",
  // Esri stops at 16. Letting the map zoom to 18 anyway and upscaling is the right trade: a
  // blurry tile still shows you which side of town an aircraft is over, whereas capping the
  // map at 16 makes pinch-zoom feel broken.
  maxNativeZoom: 16,
  maxZoom: 18,
};

/**
 * Fallback if Esri ever does what CARTO did: standard OSM raster, darkened in CSS
 * (`.map-dark .leaflet-tile` in styles.css). Keyless and about as revocation-proof as tiles
 * get, but a filtered light map never looks as good as real dark cartography — hence second.
 */
export const OSM_DARKENED: Basemap = {
  url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  maxNativeZoom: 19,
  maxZoom: 19,
};

export const BASEMAP = ESRI_DARK_GRAY;
/** Whether the CSS darkening filter applies — only the OSM fallback needs it. */
export const BASEMAP_NEEDS_DARKENING = BASEMAP === (OSM_DARKENED as Basemap);
